import { useEffect, useLayoutEffect, useRef } from "react";

import type {
  ConversationMessage,
  FeedbackDraft,
  FeedbackReason,
  FeedbackSnapshot,
} from "@go-novus/admin-api";
import {
  assistantHasFeedback,
  mountReplayMessage,
  type ReplayOptions,
} from "@go-novus/widget";

import { formatWorkspaceDateTime } from "#/shared/lib/date";

import { getLocale } from "#/paraglide/runtime";

/**
 * 买家和 AI 消息交给店面挂件那一份组件来画，商品卡、订单／邮件结果卡、Markdown 和显示规则后台不另写。
 * 颜色和圆角传后台的 CSS 变量：变量穿得进挂件的 Shadow DOM，跟着后台切深浅色，
 * 回放里也不会出现商家的主题色。
 */
const REPLAY_OPTIONS: ReplayOptions = {
  // 回放拿不到买家当时的店面语言，价格按后台界面的语言排（PRD 4.1）。
  locale: getLocale(),
  theme: {
    accentFill: "var(--bg-button-primary-default)",
    accentFillFg: "var(--content-fg-on-primary)",
    accentSoft: "var(--bg-surface-raised)",
    accentText: "var(--content-fg-strong)",
    badgeBg: "var(--bg-surface-raised)",
    badgeFg: "var(--content-fg-strong)",
    bubbleBg: "var(--bg-surface-raised)",
    bubbleRadius: "var(--radius)",
    cardRadius: "var(--radius)",
    chipRadius: "999px",
    ctrlRadius: "0.375rem",
    dangerText: "var(--content-vibrant-error-default)",
    editorial: false,
    faint: "var(--content-fg-subtle)",
    ink: "var(--content-fg-strong)",
    line: "var(--border-surface-default)",
    linkText: "var(--content-fg-strong)",
    linkUnderline: "var(--border-surface-strong)",
    muted: "var(--content-fg-subtle)",
    serif: false,
    subtle: "var(--bg-surface-raised)",
    successText: "var(--content-vibrant-success-default)",
    surfaceBg: "var(--bg-surface-base)",
    trayBg: "color-mix(in srgb, var(--content-fg-strong) 4%, transparent)",
  },
};

/** Inbox 是商家视角：买家靠左、AI 靠右。 */
const SIDE: Record<ConversationMessage["role"], string> = {
  assistant: "self-end",
  buyer: "self-start",
};

interface StorefrontMessageProps {
  message: ConversationMessage;
  groupStart: boolean;
  feedback?: {
    snapshot?: FeedbackSnapshot;
    onLoadReasons: () => Promise<FeedbackReason[]>;
    onSubmit: (draft: FeedbackDraft) => Promise<FeedbackSnapshot>;
    onLikeError?: (message: string) => void;
  };
  timeZone: string | undefined;
}

function StorefrontMessage({
  message,
  groupStart,
  feedback,
  sentAt,
  timeZone,
}: StorefrontMessageProps & { sentAt: string | undefined }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const onSubmitRef = useRef(feedback?.onSubmit);
  useEffect(() => {
    onSubmitRef.current = feedback?.onSubmit;
  }, [feedback?.onSubmit]);
  const messageSnapshot: FeedbackSnapshot | undefined =
    message.role === "assistant" && message.feedback !== null
      ? {
          messageId: message.id,
          polarity: message.feedback.rating === "positive" ? "up" : "down",
          reason: message.feedback.reasonCode,
          reasonName: message.feedback.reasonName,
          details: message.feedback.details,
          memberName: message.feedback.memberName,
          submittedAt: message.feedback.createdAt,
        }
      : undefined;
  const snapshot = feedback?.snapshot ?? messageSnapshot;
  const submittedAt = snapshot?.submittedAt ?? "";

  useLayoutEffect(() => {
    const host = hostRef.current;

    if (host === null) {
      return;
    }

    return mountReplayMessage(host, message, {
      ...REPLAY_OPTIONS,
      groupStart,
      formatDateTime:
        timeZone === undefined
          ? undefined
          : (iso) => formatWorkspaceDateTime(iso, timeZone),
      ...(feedback
        ? {
            feedback: {
              edge: "end",
              snapshot,
              onLoadReasons: feedback.onLoadReasons,
              onLikeError: feedback.onLikeError,
              sentAt:
                sentAt === undefined
                  ? undefined
                  : { label: sentAt, value: message.sentAt },
              onSubmit: (draft) => {
                const submit = onSubmitRef.current;
                if (submit === undefined)
                  return Promise.reject(new Error("failed"));
                return submit(draft);
              },
            },
          }
        : {}),
    });
    // 消息、分组、快照或时刻变了才重挂。onSubmit 每次渲染都是新函数，放进依赖会把输入中的表单拆掉。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, groupStart, submittedAt, sentAt, timeZone]);

  // 字号取店面默认的 14px：挂件卡片里的小字按它的 em 往下算，换成 13px 会掉到 11px 以下。
  // 宽度封顶：挂件的气泡按容器的 86% 排，回放栏比店面聊天窗宽一倍，不封顶一行字能拉到六百多像素。
  return (
    <div
      className={`w-full max-w-[34rem] text-body ${SIDE[message.role]}`}
      ref={hostRef}
    />
  );
}

export interface ReplayMessageProps {
  message: ConversationMessage;
  groupStart: boolean;
  /** 已按工作区时区格式化好的时刻；时区还没到手时给 undefined，这条就不摆时间。 */
  sentAt: string | undefined;
  timeZone: string | undefined;
}

/**
 * AI 回复有赞踩时，时刻跟在赞踩后面；其余时刻挂在整条消息下面。
 * 带支持卡或只有附件卡的 AI 消息没有气泡，照样有时间。
 */
export function ReplayMessage({
  message,
  groupStart,
  sentAt,
  feedback,
  timeZone,
}: ReplayMessageProps & {
  feedback?: StorefrontMessageProps["feedback"];
}) {
  const stampInActions =
    feedback !== undefined && assistantHasFeedback(message);

  return (
    <>
      <StorefrontMessage
        groupStart={groupStart}
        feedback={feedback}
        message={message}
        sentAt={stampInActions ? sentAt : undefined}
        timeZone={timeZone}
      />
      {sentAt !== undefined && !stampInActions && (
        <time
          className={`mt-1.5 text-micro text-[var(--content-fg-subtle)] ${SIDE[message.role]}`}
          dateTime={message.sentAt}
        >
          {sentAt}
        </time>
      )}
    </>
  );
}
